import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_COVER_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_COVER_SIZE = 10 * 1024 * 1024; // 10MB

function validateFile(file: File, allowedTypes: string[], maxSize: number): string | null {
  if (!allowedTypes.includes(file.type)) {
    return `${allowedTypes.map(t => t.replace("image/", "").toUpperCase()).join(" / ")} 形式のファイルのみアップロード可能です。`;
  }
  if (file.size > maxSize) {
    return `ファイルサイズは ${Math.floor(maxSize / 1024 / 1024)}MB 以下にしてください。`;
  }
  return null;
}

interface ProviderStatsProps {
  onStatsUpdated?: () => void;
}

export default function ProviderStats({ onStatsUpdated }: ProviderStatsProps) {
  const { currentUser, session, refreshProfile } = useApp();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [stats, setStats] = useState({ totalSales: 0, monthSales: 0, productCount: 0, purchaseCount: 0 });
  const [recentOrders, setRecentOrders] = useState<{ id: string; product_title: string; price: number; purchased_at: string; product_type: string }[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Profile form state
  const [serviceName, setServiceName] = useState("");
  const [bio, setBio] = useState("");
  const [slogan, setSlogan] = useState("");
  const [location, setLocation] = useState("");
  const [philosophy, setPhilosophy] = useState("");
  const [serviceContent, setServiceContent] = useState("");
  const [companyOverview, setCompanyOverview] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [capitalAmount, setCapitalAmount] = useState("");
  const [skillsInput, setSkillsInput] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [faqs, setFaqs] = useState<{ question: string; answer: string }[]>([]);
  const [profileTab, setProfileTab] = useState<"basic" | "philosophy" | "faq">("basic");

  const loadProfile = useCallback(async () => {
    if (!currentUser?.id) return;
    const { data } = await supabase.from("profiles").select("*").eq("id", currentUser.id).maybeSingle();
    if (!data) return;
    setServiceName(data.service_name || "");
    setBio(data.bio || "");
    setSlogan(data.slogan || "");
    setLocation(data.location || "");
    setPhilosophy(data.philosophy || "");
    setServiceContent(data.service_content || "");
    setCompanyOverview(data.company_overview || "");
    setEmployeeCount(data.employee_count || "");
    setCapitalAmount(data.capital_amount || "");
    setSkills(data.skills || []);
    setFaqs(data.faq || []);
    setAvatarUrl(data.avatar_url);
    setCoverUrl(data.cover_image_url);
  }, [currentUser?.id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (!currentUser?.id) return;
    const load = async () => {
      setLoadingStats(true);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const [{ data: all }, { data: month }, { count: pc }, { data: orders }] = await Promise.all([
        supabase.from("purchases").select("price").eq("provider_id", currentUser.id).eq("status", "completed"),
        supabase.from("purchases").select("price").eq("provider_id", currentUser.id).eq("status", "completed").gte("purchased_at", monthStart),
        supabase.from("products").select("id", { count: "exact", head: true }).eq("provider_id", currentUser.id),
        supabase.from("purchases").select("id,product_title,price,purchased_at,product_type").eq("provider_id", currentUser.id).eq("status", "completed").order("purchased_at", { ascending: false }).limit(8),
      ]);
      setStats({
        totalSales: (all || []).reduce((s: number, p: { price: number }) => s + p.price, 0),
        monthSales: (month || []).reduce((s: number, p: { price: number }) => s + p.price, 0),
        productCount: pc || 0,
        purchaseCount: (all || []).length,
      });
      setRecentOrders(orders || []);
      setLoadingStats(false);
    };
    load();
  }, [currentUser?.id]);

  const handleUpload = async (file: File, bucket: "avatars" | "product-images", field: "avatar_url" | "cover_image_url") => {
    if (!currentUser?.id || !session) return;
    const ext = file.name.split(".").pop();
    const path = field === "avatar_url" ? `${currentUser.id}/avatar.${ext}` : `${currentUser.id}/cover.${ext}`;
    const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
    await supabase.from("profiles").update({ [field]: publicUrl }).eq("id", currentUser.id);
    if (field === "avatar_url") setAvatarUrl(publicUrl);
    else setCoverUrl(publicUrl);
    await refreshProfile();
    return publicUrl;
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    const validationError = validateFile(file, ALLOWED_IMAGE_TYPES, MAX_AVATAR_SIZE);
    if (validationError) {
      setUploadError(validationError);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
      return;
    }
    setUploadingAvatar(true);
    try {
      await handleUpload(file, "avatars", "avatar_url");
    } catch (err) {
      setUploadError(`アップロードに失敗しました: ${(err as Error).message}`);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    const validationError = validateFile(file, ALLOWED_COVER_TYPES, MAX_COVER_SIZE);
    if (validationError) {
      setUploadError(validationError);
      if (coverInputRef.current) coverInputRef.current.value = "";
      return;
    }
    setUploadingCover(true);
    try {
      await handleUpload(file, "product-images", "cover_image_url");
    } catch (err) {
      setUploadError(`アップロードに失敗しました: ${(err as Error).message}`);
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const addSkill = () => {
    const trimmed = skillsInput.trim();
    if (trimmed && !skills.includes(trimmed)) setSkills([...skills, trimmed]);
    setSkillsInput("");
  };

  const addFaq = () => setFaqs([...faqs, { question: "", answer: "" }]);
  const updateFaq = (i: number, field: "question" | "answer", val: string) => {
    setFaqs(faqs.map((f, idx) => idx === i ? { ...f, [field]: val } : f));
  };
  const removeFaq = (i: number) => setFaqs(faqs.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!currentUser?.id) return;
    setSaving(true);
    await supabase.from("profiles").update({
      service_name: serviceName,
      bio, slogan, location, philosophy,
      service_content: serviceContent,
      company_overview: companyOverview,
      employee_count: employeeCount,
      capital_amount: capitalAmount,
      skills,
      faq: faqs.filter(f => f.question.trim()),
    }).eq("id", currentUser.id);
    await refreshProfile();
    if (onStatsUpdated) onStatsUpdated();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const profileTabs = [
    { id: "basic" as const, label: "基本情報", icon: "ri-user-line" },
    { id: "philosophy" as const, label: "理念・想い", icon: "ri-plant-line" },
    { id: "faq" as const, label: "よくある質問", icon: "ri-question-line" },
  ];

  return (
    <div className="space-y-6">
      {uploadError && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-start gap-2.5">
          <i className="ri-error-warning-line text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-red-500 font-sans">{uploadError}</p>
            <p className="text-xs text-red-400/70 font-sans mt-0.5">JPEG / PNG / WebP 形式のみ対応しています。</p>
          </div>
          <button onClick={() => setUploadError(null)} className="text-red-300 hover:text-red-400 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Profile Edit Card */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        {/* Cover Image */}
        <div className="relative h-40 bg-stone-100 overflow-hidden group">
          {coverUrl ? (
            <img src={coverUrl} alt="カバー" className="w-full h-full object-cover object-top" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-charcoal/30">
              <i className="ri-image-add-line text-3xl" />
              <p className="text-xs font-sans">カバー画像を追加</p>
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
            <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverUpload} className="hidden" />
            <button
              onClick={() => coverInputRef.current?.click()}
              disabled={uploadingCover}
              className="flex items-center gap-2 bg-white text-charcoal text-xs px-4 py-2 rounded-full cursor-pointer hover:bg-ivory transition-colors whitespace-nowrap"
            >
              {uploadingCover ? <><i className="ri-loader-4-line animate-spin" />アップロード中...</> : <><i className="ri-camera-line" />カバー画像を変更</>}
            </button>
          </div>
        </div>

        <div className="px-6 pb-6">
          {/* Avatar */}
          <div className="flex items-end justify-between -mt-10 mb-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-white bg-stone-100">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="アバター" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><i className="ri-store-2-line text-2xl text-charcoal/30" /></div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-2xl">
                    <i className="ri-loader-4-line animate-spin text-white" />
                  </div>
                )}
              </div>
              <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleAvatarUpload} className="hidden" />
              <button
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute -bottom-2 -right-2 w-7 h-7 flex items-center justify-center bg-teal text-white rounded-full cursor-pointer hover:bg-deep-green transition-colors flex-shrink-0"
                title="アバター変更"
              >
                <i className="ri-camera-line text-xs" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              {saved && (
                <span className="text-xs text-teal font-sans flex items-center gap-1">
                  <i className="ri-checkbox-circle-line" />保存しました
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin" />保存中...</> : "変更を保存"}
              </button>
            </div>
          </div>

          {/* Sub-tab nav */}
          <div className="flex gap-1 mb-5 bg-stone-50 rounded-xl p-1">
            {profileTabs.map(t => (
              <button
                key={t.id}
                onClick={() => setProfileTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-sans rounded-lg cursor-pointer transition-all ${profileTab === t.id ? "bg-white text-deep-green font-medium" : "text-charcoal/50 hover:text-charcoal"}`}
              >
                <i className={t.icon} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>

          {/* Basic Info */}
          {profileTab === "basic" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1">サービス名・屋号 <span className="text-red-400">*</span></label>
                  <input value={serviceName} onChange={e => setServiceName(e.target.value)} placeholder="例：○○工房 田中、△△職人教室" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1">活動エリア・所在地</label>
                  <input value={location} onChange={e => setLocation(e.target.value)} placeholder="例：京都府京都市" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1">キャッチコピー（スローガン）</label>
                <input value={slogan} onChange={e => setSlogan(e.target.value)} maxLength={80} placeholder="例：職人の技術を、あなたの手に。" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1">自己紹介・プロフィール</label>
                <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={500} rows={3} placeholder="あなたの技術や経験について紹介してください（500文字以内）" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors" />
                <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{bio.length}/500</p>
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-2">得意技術・専門スキル</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {skills.map((s, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs bg-teal/10 text-teal px-3 py-1 rounded-full font-sans">
                      {s}
                      <button onClick={() => setSkills(skills.filter((_, idx) => idx !== i))} className="cursor-pointer hover:text-red-400 transition-colors ml-0.5"><i className="ri-close-line" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={skillsInput} onChange={e => setSkillsInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} placeholder="例：京友禅染め、木工、陶芸（Enterで追加）" className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
                  <button onClick={addSkill} className="px-4 py-2.5 rounded-xl bg-teal/10 text-teal text-sm font-sans cursor-pointer hover:bg-teal hover:text-white transition-colors whitespace-nowrap">追加</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1">従業員数（任意）</label>
                  <input value={employeeCount} onChange={e => setEmployeeCount(e.target.value)} placeholder="例：1名（個人）" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1">資本金（任意）</label>
                  <input value={capitalAmount} onChange={e => setCapitalAmount(e.target.value)} placeholder="例：300万円" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1">サービス内容（任意）</label>
                  <input value={serviceContent} onChange={e => setServiceContent(e.target.value)} placeholder="例：陶芸・木工・食品加工など" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors" />
                </div>
              </div>
            </div>
          )}

          {/* Philosophy */}
          {profileTab === "philosophy" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1">経営理念・想い</label>
                <textarea value={philosophy} onChange={e => setPhilosophy(e.target.value)} maxLength={1000} rows={6} placeholder="あなたのサービスへの想いや、なぜこの技術を広めたいか…（1000文字以内）" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors" />
                <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{philosophy.length}/1000</p>
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1">会社概要（任意）</label>
                <textarea value={companyOverview} onChange={e => setCompanyOverview(e.target.value)} maxLength={500} rows={3} placeholder="会社・事業の概要を入力してください（500文字以内）" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors" />
              </div>
              <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                <p className="text-xs font-sans text-charcoal/50 flex items-start gap-2">
                  <i className="ri-lightbulb-line text-gold flex-shrink-0 mt-0.5" />
                  理念や想いを充実させることで、受講者に信頼感を与え、フォロワー数・購入率の向上につながります。
                </p>
              </div>
            </div>
          )}

          {/* FAQ */}
          {profileTab === "faq" && (
            <div className="space-y-4">
              <p className="text-xs text-charcoal/50 font-sans">事業者ページに表示される「よくある質問」を設定します。デフォルトのFAQも自動で表示されます。</p>
              {faqs.map((faq, i) => (
                <div key={i} className="bg-stone-50 rounded-xl p-4 border border-stone-100 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-sans text-gold font-medium">Q{i + 1}.</span>
                    <button onClick={() => removeFaq(i)} className="text-charcoal/30 hover:text-red-400 cursor-pointer transition-colors flex-shrink-0">
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                  <input value={faq.question} onChange={e => updateFaq(i, "question", e.target.value)} placeholder="よくある質問（例：発送はどのくらいかかりますか？）" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors bg-white" />
                  <textarea value={faq.answer} onChange={e => updateFaq(i, "answer", e.target.value)} rows={2} placeholder="回答（例：ご注文から3〜5営業日以内に発送します。）" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors bg-white" />
                </div>
              ))}
              <button onClick={addFaq} className="w-full py-3 rounded-xl border-2 border-dashed border-stone-200 text-sm text-charcoal/50 font-sans hover:border-teal hover:text-teal transition-colors cursor-pointer flex items-center justify-center gap-2">
                <i className="ri-add-line" />FAQを追加する
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      {loadingStats ? (
        <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "今月の売上", value: `¥${stats.monthSales.toLocaleString()}`, icon: "ri-money-yen-circle-line", color: "text-gold" },
              { label: "累計売上", value: `¥${stats.totalSales.toLocaleString()}`, icon: "ri-bar-chart-line", color: "text-deep-green" },
              { label: "出品数", value: `${stats.productCount}点`, icon: "ri-store-2-line", color: "text-teal" },
              { label: "総購入件数", value: `${stats.purchaseCount}件`, icon: "ri-shopping-bag-line", color: "text-gold" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-5 border border-stone-100">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${s.icon} ${s.color} text-2xl`} />
                </div>
                <p className="font-serif text-2xl text-charcoal mt-2">{s.value}</p>
                <p className="text-xs text-charcoal/50 font-sans mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="p-5 border-b border-stone-100">
              <h3 className="font-serif text-deep-green">最近の注文</h3>
            </div>
            {recentOrders.length === 0 ? (
              <div className="py-12 text-center">
                <i className="ri-shopping-bag-line text-4xl text-charcoal/20 block mb-3" />
                <p className="text-sm text-charcoal/40 font-sans">まだ注文がありません</p>
                <p className="text-xs text-charcoal/30 font-sans mt-1">商品を出品して最初の販売を目指しましょう</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {recentOrders.map((o) => (
                  <div key={o.id} className="px-5 py-4 flex items-center gap-4">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${o.product_type === "video" ? "bg-teal/10" : "bg-gold/10"}`}>
                      <i className={`${o.product_type === "video" ? "ri-play-circle-line text-teal" : "ri-box-3-line text-gold"} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-sans text-charcoal truncate">{o.product_title}</p>
                      <p className="text-xs text-charcoal/40 font-sans">{new Date(o.purchased_at).toLocaleDateString("ja-JP")}</p>
                    </div>
                    <p className="font-sans text-sm text-charcoal font-medium whitespace-nowrap">¥{o.price.toLocaleString()}</p>
                    <span className="text-xs bg-deep-green/10 text-deep-green px-2 py-0.5 rounded-full font-sans whitespace-nowrap">完了</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
